import { useEffect, useRef, useState } from 'react';
import EditIcon from '../../../public/icons/EditIcon';
import { useToken } from '../auth/useToken';
import { safeJwtDecode } from '../../util/jwtUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/backdrop.css';
import 'tippy.js/animations/scale.css';
import 'tippy.js/animations/perspective.css';
import 'tippy.js/animations/shift-away.css';
import Modal from '../Modal';
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from 'react-image-crop';
import { useProfileImage } from '../../hooks/useProfileImage';
import useUserDataStore from '../../store/userDataStore';
import CSS from '../pages/Settings/SettingsPage.module.css';

export default function AccountSettingsLayout({
  handleFileChange,
  togglePicModal,
  openPicModal,
  image,
  setImage,
  error,
  setError,
  ASPECT_RATIO,
  MIN_DIMENSION,
  onCroppedImageSet,
  onRemovePhoto,
  imageToRemove,
}) {
  const [token] = useToken();
  const [email, setEmail] = useState('');
  const [crop, setCrop] = useState();
  const [profilePicture, setProfilePicture] = useState(
    'default-profile-pic.png'
  );
  const userData = useUserDataStore((state) => state.userData);

  const imageRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // Set profile pic link to profilePicture state
  useEffect(() => {
    if (imageToRemove) {
      setProfilePicture('default-profile-pic.png');
    } else if (userData.image) {
      setProfilePicture(userData.image);
    }
  }, [userData.image, imageToRemove]);

  // Tippy ref to access Tippy instance
  const tippyRef = useRef(null);

  // Get email value from token and set it to local email state
  useEffect(() => {
    const decodedToken = safeJwtDecode(token);
    if (decodedToken && decodedToken.email) {
      setEmail(decodedToken.email);
    }
  }, [token]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const cropWidthInPercent = (MIN_DIMENSION / width) * 100;

    const crop = makeAspectCrop(
      {
        unit: '%',
        width: cropWidthInPercent,
      },
      ASPECT_RATIO,
      width,
      height
    );
    const centeredCrop = centerCrop(crop, width, height);
    setCrop(centeredCrop);
  };

  // Set cropped section from image to canvas
  const setImageCrop = (image, canvas, crop) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    const pixelRatio = window.devicePixelRatio;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';
    ctx.save();

    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;

    // Move the crop origin to the canvas origin(0,0)
    ctx.translate(-cropX, -cropY);
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight
    );

    ctx.restore();
  };

  // Add cropped selection to profile pic display
  const updateProfilePicUrl = (imgSrc) => {
    setProfilePicture(imgSrc);
  };

  //Remove profile pic - just set state, don't call API
  const removeProfilePic = () => {
    // Only proceed if there's an actual profile image to remove
    if (
      userData.image &&
      userData.image !== 'default-profile-pic.png' &&
      onRemovePhoto
    ) {
      onRemovePhoto();
      setProfilePicture('default-profile-pic.png');
      setImage(null);
      if (tippyRef.current) {
        tippyRef.current.hide();
      }
    }
  };

  return (
    <>
      <p className={CSS.sectionHeading}>Account settings</p>
      <div className='profile-pic-and-name-wrapper'>
        <div className='profile-pic-wrapper'>
          {/* Use profilePicture state for the src */}
          <div
            style={{
              background: 'white',
              width: '100%',
              height: '100%',
              borderRadius: '500px',
            }}>
            <img src={profilePicture} alt='profile picture' />
          </div>

          <Tippy
            content={
              <div className='tippy-drop-down-wrapper'>
                <p
                  className='tippy-selectable-text'
                  onClick={(e) => {
                    if (e.target.tagName !== 'LABEL') {
                      document.getElementById('profilePic').click();
                    }
                  }}>
                  <label htmlFor='profilePic' style={{ cursor: 'pointer' }}>
                    Upload a photo
                  </label>
                  <input
                    type='file'
                    onChange={handleFileChange}
                    id='profilePic'
                    accept='image/*'
                    style={{ display: 'none' }}
                  />
                </p>
                {/* Only show Remove photo option if there's an actual profile image */}
                {userData.image &&
                  userData.image !== 'default-profile-pic.png' &&
                  !imageToRemove && (
                    <p
                      className='tippy-selectable-text'
                      onClick={removeProfilePic}>
                      Remove photo
                    </p>
                  )}
              </div>
            }
            className='edit-profilePic-drop-down'
            placement='bottom-start'
            // animation={'shift-away'}
            interactive={true}
            trigger='click'
            onCreate={(instance) => {
              tippyRef.current = instance; // Store Tippy instance in ref
            }}>
            <div className='edit-profile-pic' title='Edit profile photo'>
              <EditIcon svgClass='edit-profile-pic-icon' />
            </div>
          </Tippy>
        </div>
        <p className='profile-name'>{email}</p>
      </div>

      {openPicModal && (
        <Modal open={openPicModal} toggleModal={togglePicModal}>
          <div className='crop-modal-wrapper'>
            <p
              className='section-heading'
              style={{ marginTop: '0', color: 'var(--blue-text--bold)' }}>
              Crop image
            </p>
            {error && <p className='crop-error-msg'>{error}</p>}
            {!error && (
              <div className='react-crop-wrapper'>
                <ReactCrop
                  crop={crop}
                  onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
                  circularCrop
                  keepSelection
                  aspect={ASPECT_RATIO}
                  minWidth={MIN_DIMENSION}>
                  <img
                    ref={imageRef}
                    className={'crop-profile-pic'}
                    src={image}
                    alt='selected image'
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
            )}
            {crop && (
              <canvas
                ref={previewCanvasRef}
                style={{
                  display: 'none',
                  objectFit: 'contain',
                  width: '150px',
                  height: '150px',
                }}
              />
            )}
            <div className='crop-buttons-wrapper'>
              <button
                className='crop-btn'
                onClick={(e) => {
                  setImageCrop(
                    imageRef.current,
                    previewCanvasRef.current,
                    convertToPixelCrop(
                      crop,
                      imageRef.current.width,
                      imageRef.current.height
                    )
                  );
                  const dataUrl = previewCanvasRef.current.toDataURL();
                  updateProfilePicUrl(dataUrl);

                  // Call the callback to notify parent component about the cropped image
                  if (onCroppedImageSet) {
                    onCroppedImageSet(dataUrl);
                  }

                  togglePicModal(e);
                }}
                style={
                  error
                    ? {
                        pointerEvents: 'none',
                        opacity: '0.6',
                        cursor: 'initial',
                      }
                    : {
                        pointerEvents: 'visible',
                        opacity: '1',
                        cursor: 'pointer',
                      }
                }>
                Apply crop
              </button>
              <button
                className='crop-btn crop-btn-cancel'
                onClick={togglePicModal}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
